import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';
import { PERMISSION_LABELS } from '../utils/permissions';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getAuthHeaders = () => {
  const token = getFromLocalStorage('accessToken');
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
};

const StaffForm = ({ staff, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: staff?.name || '', email: staff?.email || '', password: '',
    phone: staff?.phone || '', role: staff?.role || 'sales',
    isActive: staff?.isActive ?? true,
    permissions: staff?.permissions || [],
    primaryBranchId: staff?.primaryBranchId || '',
    accessibleBranchIds: staff?.accessibleBranchIds || [],
  });
  const [roleDefaults, setRoleDefaults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [archivedRecord, setArchivedRecord] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/staff/permissions-list`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setRoleDefaults(d.roleDefaults || {}))
      .catch((e) => console.error('Load permissions error:', e));
    fetch(`${BACKEND_URL}/api/branches`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setBranchOptions(d.branches || []))
      .catch((e) => console.error('Load branches error:', e));
  }, []);

  useEffect(() => {
    if (!staff && roleDefaults[form.role]) {
      setForm(f => ({ ...f, permissions: roleDefaults[form.role] }));
    }
  }, [form.role, roleDefaults, staff]);

  const togglePerm = (p) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p]
    }));
  };

  const handleRestore = async (archivedId) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/${archivedId}/restore`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Geri yükleme başarısız');
      }
      toast({ title: "Personel Geri Yüklendi", description: "Arşivlenmiş personel başarıyla geri yüklendi." });
      setArchivedRecord(null);
      if (onSave) onSave();
    } catch (e) {
      setError(e.message);
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const parseDetail = (detail) => {
    if (typeof detail === 'object' && detail?.code === 'ARCHIVED_EMAIL') return detail;
    if (typeof detail === 'string') return { message: detail };
    return { message: JSON.stringify(detail) };
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || (!staff && !form.password)) {
      setError('Ad, email ve şifre zorunludur.');
      toast({ title: "Hata", description: "Ad, email ve şifre zorunludur.", variant: "destructive" }); return;
    }
    setLoading(true);
    setError('');
    setArchivedRecord(null);
    try {
      const isEdit = !!staff;
      const url = isEdit ? `${BACKEND_URL}/api/staff/${staff.id}` : `${BACKEND_URL}/api/staff`;
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const parsed = parseDetail(e.detail);
        if (parsed.code === 'ARCHIVED_EMAIL') {
          setArchivedRecord(parsed);
          setError(parsed.message);
          return;
        }
        throw new Error(parsed.message || `İşlem başarısız (${res.status})`);
      }
      toast({ title: isEdit ? "Personel Güncellendi" : "Personel Oluşturuldu" });
      if (onSave) onSave();
    } catch (e) {
      setError(e.message);
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="staff-form">
      {error && (
        <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-400 text-sm" data-testid="staff-form-error">
          {error}
          {archivedRecord && (
            <button type="button" onClick={() => handleRestore(archivedRecord.archivedId)}
              disabled={loading}
              className="mt-2 w-full bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded-md py-1.5 px-3 flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="staff-restore-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
              {archivedRecord.archivedName ? `"${archivedRecord.archivedName}" Geri Yükle` : 'Arşivlenen Kaydı Geri Yükle'}
            </button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-gray-400 text-xs mb-1 block">Ad Soyad</label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="staff-name-input" />
        </div>
        <div>
          <label className="text-gray-400 text-xs mb-1 block">Email</label>
          <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm" disabled={!!staff} data-testid="staff-email-input" />
        </div>
        <div>
          <label className="text-gray-400 text-xs mb-1 block">Telefon</label>
          <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
        </div>
        {!staff && (
          <div>
            <label className="text-gray-400 text-xs mb-1 block">Sifre</label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="staff-password-input" />
          </div>
        )}
        <div>
          <label className="text-gray-400 text-xs mb-1 block">Rol</label>
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-md h-8 text-sm px-2" data-testid="staff-role-select">
            <option value="sales">Satis</option>
            <option value="technician">Uygulamaci</option>
            <option value="manager">Yonetici</option>
          </select>
        </div>
        {staff && (
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                className="rounded" />
              <span className="text-white text-sm">Aktif</span>
            </label>
          </div>
        )}
      </div>

      {/* Branch Assignment */}
      <div className="space-y-2">
        <label className="text-gray-400 text-xs">Sube Atamasi</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-[10px] mb-1 block">Ana Sube</label>
            <select value={form.primaryBranchId} onChange={e => {
              const bid = e.target.value;
              setForm(f => {
                const acc = f.accessibleBranchIds.includes(bid) ? f.accessibleBranchIds : [...f.accessibleBranchIds, bid];
                return { ...f, primaryBranchId: bid, accessibleBranchIds: acc };
              });
            }} className="w-full bg-gray-800 border border-gray-700 text-white rounded-md h-8 text-sm px-2" data-testid="staff-primary-branch">
              <option value="">Secin</option>
              {branchOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-[10px] mb-1 block">Erisebilir Subeler</label>
            <div className="flex flex-wrap gap-1.5 bg-gray-800 border border-gray-700 rounded-md p-2 min-h-[32px]">
              {branchOptions.map(b => (
                <label key={b.id} className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.accessibleBranchIds.includes(b.id)}
                    onChange={(e) => {
                      setForm(f => ({
                        ...f,
                        accessibleBranchIds: e.target.checked
                          ? [...f.accessibleBranchIds, b.id]
                          : f.accessibleBranchIds.filter(x => x !== b.id)
                      }));
                    }} className="rounded w-3 h-3" />
                  <span className={form.accessibleBranchIds.includes(b.id) ? 'text-white' : 'text-gray-500'}>{b.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-gray-400 text-xs">Yetkiler</label>
          <Button size="sm" variant="outline" className="text-xs h-6 border-gray-600 text-gray-300"
            onClick={() => setForm(f => ({ ...f, permissions: roleDefaults[f.role] || [] }))}>
            Varsayilana Don
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto bg-gray-800/50 rounded-lg p-2 border border-gray-700">
          {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center gap-1.5 cursor-pointer text-xs py-0.5" data-testid={`perm-${key}`}>
              <input type="checkbox" checked={form.permissions.includes(key)}
                onChange={() => togglePerm(key)} className="rounded w-3 h-3" />
              <span className={form.permissions.includes(key) ? 'text-white' : 'text-gray-500'}>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-brand hover:bg-brand" data-testid="save-staff-btn">
          {loading ? 'Kaydediliyor...' : (staff ? 'Guncelle' : 'Olustur')}
        </Button>
        <Button onClick={onCancel} disabled={loading} variant="outline" className="border-gray-600 text-gray-300">Iptal</Button>
      </div>
    </div>
  );
};

export default StaffForm;
